import React, { useState } from 'react';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import { WorkflowRun, WorkflowRunStatus } from '@/types/agents';
import RunDetailsModal from './RunDetailsModal';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';

// Mock data - replace with actual API calls
const mockRuns: WorkflowRun[] = [
    {
        id: '1',
        date: '2024-01-01T10:30:00Z',
        workflowName: 'Customer Support Workflow',
        status: 'Successful',
        message: 'Workflow completed successfully',
        details: {
            startTime: '2024-01-01T10:30:00Z',
            endTime: '2024-01-01T10:35:00Z',
            duration: 300,
            steps: [
                { id: '1', name: 'Initial Analysis', status: 'Successful', duration: 45 },
                { id: '2', name: 'Data Processing', status: 'Successful', duration: 120 },
                { id: '3', name: 'Response Generation', status: 'Successful', duration: 135 },
            ],
        },
    },
    {
        id: '2',
        date: '2024-01-01T11:00:00Z',
        workflowName: 'Data Processing Pipeline',
        status: 'Failed',
        message: 'Error in step 2: Connection timeout',
        details: {
            startTime: '2024-01-01T11:00:00Z',
            endTime: '2024-01-01T11:02:30Z',
            duration: 150,
            error: 'Connection timeout after 150 seconds',
            steps: [
                { id: '1', name: 'Data Fetch', status: 'Successful', duration: 30 },
                { id: '2', name: 'Data Transform', status: 'Failed', duration: 120, error: 'Connection timeout' },
            ],
        },
    },
    {
        id: '3',
        date: '2024-01-01T11:15:00Z',
        workflowName: 'Customer Support Workflow',
        status: 'Running',
        message: 'Workflow is currently running',
    },
    {
        id: '4',
        date: '2024-01-01T09:45:00Z',
        workflowName: 'Report Generation',
        status: 'Successful',
        message: 'Report generated successfully',
    },
];

const WorkflowRunsTable: React.FC = () => {
    const [selectedRun, setSelectedRun] = useState<WorkflowRun | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatusIcon = (status: WorkflowRunStatus) => {
        switch (status) {
            case 'Successful':
                return <CheckCircle2 size={16} className="text-secondary" />;
            case 'Failed':
                return <XCircle size={16} className="text-destructive" />;
            case 'Running':
                return <Clock size={16} className="text-primary" />;
            default:
                return null;
        }
    };

    const getStatusColor = (status: WorkflowRunStatus) => {
        switch (status) {
            case 'Successful':
                return 'hsl(var(--secondary))';
            case 'Failed':
                return 'hsl(var(--destructive))';
            case 'Running':
                return 'hsl(var(--primary))';
            default:
                return 'hsl(var(--muted-foreground))';
        }
    };

    const handleRowClick = (run: WorkflowRun) => {
        setSelectedRun(run);
        setIsModalOpen(true);
    };

    return (
        <>
            <div
                className="overflow-x-auto rounded-lg shadow bg-background"
            >
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-primary">Date</TableHead>
                            <TableHead className="text-primary">Workflow Name</TableHead>
                            <TableHead className="text-primary">Status</TableHead>
                            <TableHead className="text-primary">Message</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {mockRuns.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center py-8">
                                    No workflow runs found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            mockRuns.map((run) => (
                                <TableRow
                                    key={run.id}
                                    className="cursor-pointer hover:bg-muted/10 transition-colors"
                                    onClick={() => handleRowClick(run)}
                                >
                                    <TableCell className="text-foreground">
                                        {formatDate(run.date)}
                                    </TableCell>
                                    <TableCell className="text-foreground">
                                        {run.workflowName}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            {getStatusIcon(run.status)}
                                            <span style={{ color: getStatusColor(run.status) }}>
                                                {run.status}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {run.message}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {selectedRun && (
                <RunDetailsModal
                    run={selectedRun}
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setSelectedRun(null);
                    }}
                />
            )}
        </>
    );
};

export default WorkflowRunsTable;

